import { Box, Card, withStyles } from 'vopay';
import { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import { useToast } from '@/providers/Toast.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useForm } from '@/components/Form/utils';
import { useUserSettingsFields, UserSettingsFormValues } from './useUserSettingsFields';
import styles from './UserSettings.module.scss';
import { Button, Title } from 'UI';
import { DEFAULT_SUB_ACCOUNT_BALANCE_DISPLAY } from '@/hooks/useApi/useUserSettings';

const classes = withStyles(styles);

const UserSettingsContainer: FunctionComponent = () => {
  const toast = useToast();
  const { t } = useTranslation('translations', { keyPrefix: 'User.Settings' });
  const { response } = useClientData();
  const { userSettings, submitUserSettings } = useUserSettingsProvider();

  const defaultValues: UserSettingsFormValues = {
    Timezone: userSettings.timezone || 'UTC',
    SubAccountBalanceDisplay: userSettings.SubAccountBalanceDisplay || DEFAULT_SUB_ACCOUNT_BALANCE_DISPLAY,
  };

  const fieldDataList = useUserSettingsFields();

  const { renderFormElements, ctx } = useForm<UserSettingsFormValues>({
    config: fieldDataList,
    initialValues: defaultValues,
    onSubmit: async (values) => {
      const submitUserSettingsResponse = await submitUserSettings({
        EmailAddress: response.LoginUserName,
        ...values,
      });
      if (!submitUserSettingsResponse.Success) {
        toast.neutral(submitUserSettingsResponse.ErrorMessage);
      } else {
        toast.success(t('successMessage'));
      }
    },
  });

  const hasChanges = useMemo(
    () =>
      userSettings.timezone !== ctx.values.Timezone ||
      userSettings.SubAccountBalanceDisplay !== ctx.values.SubAccountBalanceDisplay,
    [
      userSettings.timezone,
      userSettings.SubAccountBalanceDisplay,
      ctx.values.Timezone,
      ctx.values.SubAccountBalanceDisplay,
    ],
  );

  return (
    <form onSubmit={ctx.handleSubmit} className={classes('form')}>
      <Box flx="col" gap="lg" base="8">
        <Title level="1">{t('title')}</Title>
        <Card style={{ padding: '24px' }}>
          <div className={classes('rows')}>
            {renderFormElements({
              config: fieldDataList,
              override: (fieldData) => {
                return (
                  <div className={classes('row')}>
                    <div className={classes('label')}>
                      {fieldData.label} {fieldData.tooltip}
                    </div>
                    <div className={classes('input')}>{fieldData.template}</div>
                  </div>
                );
              },
            })}
          </div>
        </Card>

        <Box jc="fe">
          <Button id="save-user-settings" type="submit" colorScheme="primary" disabled={!hasChanges || !ctx.isValid}>
            {t('saveButton')}
          </Button>
        </Box>
      </Box>
    </form>
  );
};

export default UserSettingsContainer;
